import React, { useEffect, useState, Fragment, forwardRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Pagination,
  Typography,
} from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import IconLibrary from './IconLibrary';

export const EMPTY_ITEMS = [{ label: 'Please Select', value: -1 }];
export const ALL_ITEMS = [{ label: 'All', value: '' }];

const ListBoxSelectorStatic = forwardRef(
  (
    {
      height = '400px',
      getLeftItems = () => [],
      getRightItems = () => [],
      onItemsAdded = () => undefined,
      onItemsRemoved = () => undefined,
      leftTitle = null,
      rightTitle = null,
      parentId = null,
      onChange = null,
      hasSelectAll = true,
      rightInitial = [],
    },
    ref,
  ) => {
    const [left, setLeft] = useState([]);
    const [right, setRight] = useState([]);
    const [selectedLeft, setSelectedLeft] = useState([]);
    const [selectedRight, setSelectedRight] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [isAll, setisAll] = useState(false);

    const isRightSideAll = items => {
      return (
        Array.isArray(items?.data) &&
        items.data.length === 1 &&
        items.data[0].value === ''
      );
    };

    const filterItems = (source, destination) => {
      if (Array.isArray(destination) && Array.isArray(source)) {
        return destination?.filter(
          destItem => !source.some(srcItem => srcItem.value === destItem.value),
        );
      }
      return destination;
    };

    const fetchData = async (leftPageNumber = 1, rightPageNumber = 1) => {
      const leftItems = await getLeftItems(leftPageNumber);
      const rightItems = getRightItems(rightPageNumber);

      const newLeftItemsData = filterItems(rightItems.data, leftItems.data);
      const newLeftItems = {
        ...leftItems,
        data: newLeftItemsData,
      };
      setLeft(newLeftItems);
      setRight(rightItems);
      setisAll(isRightSideAll(rightItems));
    };

    const handleToggle = (side, value) => {
      if (side === 'left') {
        setSelectedLeft(prev => {
          return prev?.length > 0 && prev.some(i => i.value === value?.value)
            ? prev.filter(v => v.value !== value.value)
            : [...prev, value];
        });
      } else {
        setSelectedRight(prev => {
          return prev?.length > 0 && prev.some(i => i.value === value?.value)
            ? prev.filter(v => v.value !== value.value)
            : [...prev, value];
        });
      }
    };

    const handleSelectAllItems = async () => {
      setIsSaving(true);
      setSelectedLeft([]);
      await onItemsAdded(ALL_ITEMS);
      await fetchData();
      setIsSaving(false);
    };

    const handleDeslectAllItems = async () => {
      setIsSaving(true);
      setSelectedLeft([]);
      await onItemsRemoved(EMPTY_ITEMS);
      await fetchData();
      setIsSaving(false);
    };

    const handleAllRight = async () => {
      setIsSaving(true);
      setSelectedLeft([]);
      await onItemsAdded(left.data);
      await fetchData();
      setIsSaving(false);
    };

    const handleAllLeft = async () => {
      setIsSaving(true);
      setSelectedRight([]);
      await onItemsRemoved(right.data);
      await fetchData();
      setIsSaving(false);
    };

    const handleCheckedRight = async () => {
      setIsSaving(true);
      await onItemsAdded(selectedLeft);
      await fetchData();
      setSelectedLeft([]);
      setIsSaving(false);
    };

    const handleCheckedLeft = async () => {
      setIsSaving(true);
      await onItemsRemoved(selectedRight);
      await fetchData();
      setSelectedRight([]);
      setIsSaving(false);
    };

    useEffect(() => {
      if (onChange) {
        onChange(parentId, right);
      }
    }, [parentId, right]);

    useEffect(() => {
      fetchData();
    }, [parentId, rightInitial]);

    const customList = (customItems, selected, side) => {
      const pageCount = customItems?.totalPages || 0;
      return (
        <Box>
          <Box
            border={1}
            borderColor="divider"
            borderRadius={1}
            sx={{ height, overflow: 'auto' }}>
            <List dense component="div">
              {customItems?.data?.map((item, idx) => (
                <Fragment key={item.value?.id || item.value}>
                  <ListItem dense disableGutters>
                    <ListItemButton
                      alignItems="flex-start"
                      onClick={() => handleToggle(side, item)}
                      selected={selected.some(s => s.value === item.value)}>
                      <ListItemText primary={item.label} />
                    </ListItemButton>
                  </ListItem>
                  {idx < (customItems?.data?.length || 0) - 1 && <Divider />}
                </Fragment>
              ))}
            </List>
          </Box>
          {pageCount > 1 && (
            <Pagination
              page={customItems?.page || 1}
              onChange={async (event, value) => {
                if (side === 'left') {
                  const newItems = await getLeftItems(value);
                  setLeft(newItems);
                } else {
                  const newItems = await getRightItems(value);
                  setRight(newItems);
                }
              }}
              sx={{
                mt: 1,
                mr: 1,
                ml: 1,
                justifyContent: 'flex-end',
                display: 'flex',
              }}
              count={pageCount}
              size="small"
            />
          )}
        </Box>
      );
    };

    return (
      <Box ref={ref} sx={{ marginTop: 2, marginBottom: 2 }}>
        {leftTitle && rightTitle && (
          <Grid2
            container
            spacing={2}
            textAlign="left"
            justifyContent="center"
            alignItems="center">
            <Grid2 md={5}>
              <Typography variant="body1" sx={{ fontWeight: 500 }} gutterBottom>
                {leftTitle}
              </Typography>
            </Grid2>
            <Grid2 md={2}>&nbsp;</Grid2>
            <Grid2 md={5}>
              <Typography variant="body1" sx={{ fontWeight: 500 }} gutterBottom>
                {rightTitle}
              </Typography>
            </Grid2>
          </Grid2>
        )}
        <Grid2
          container
          spacing={2}
          justifyContent="center"
          alignItems="center">
          <Grid2 md={5}>{customList(left, selectedLeft, 'left')}</Grid2>
          <Grid2 md={2}>
            {isSaving ? (
              <Box mb={3}>
                <CircularProgress label="Saving" size={50} />
                <Typography variant="body1" fontWeight="bold" sx={{ mt: 1 }}>
                  Saving
                </Typography>
              </Box>
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={1}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCheckedRight}
                  disabled={selectedLeft?.length === 0 || isSaving || isAll}>
                  <IconLibrary.MoveRight />
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAllRight}
                  disabled={left?.data?.length === 0 || isSaving || isAll}
                  sx={!hasSelectAll ? { marginBottom: '30px' } : undefined}>
                  <IconLibrary.MoveAllRight />
                </Button>
                {hasSelectAll && (
                  <Button
                    sx={{ marginBottom: '30px' }}
                    variant="outlined"
                    size="small"
                    onClick={handleSelectAllItems}
                    disabled={isSaving || isAll}>
                    Select All
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCheckedLeft}
                  disabled={selectedRight?.length === 0 || isSaving}>
                  <IconLibrary.MoveLeft />
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAllLeft}
                  disabled={right?.data?.length === 0 || isSaving}>
                  <IconLibrary.MoveAllLeft />
                </Button>
                {hasSelectAll && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleDeslectAllItems}
                    disabled={right?.data?.length === 0 || isSaving}>
                    Deselect All
                  </Button>
                )}
              </Box>
            )}
          </Grid2>
          <Grid2 md={5}>{customList(right, selectedRight, 'right')}</Grid2>
        </Grid2>
      </Box>
    );
  },
);

export default ListBoxSelectorStatic;
